import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Wallet,
  TrendingUp,
  TrendingDown,
  PiggyBank,
  Filter,
  ArrowUpRight,
  Receipt,
  Tag,
  Layers,
  DollarSign,
  FileText
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { groupBy } from 'lodash';
import api from '../api/api';
import TransactionItem from '../components/TransactionItem.js';
import IncomeItem from '../components/IncomeItem';
import {
  setSavings,
  setSortedIncomes,
  setSortedTransactions,
  setMonthlyIncome,
  setTransactions,
  setCurrentMonth,
  setBalance,
  setSelectedDate,
  setIncomeArray,
  setCategoryArray,
  setLabelArray
} from '../store/transactionSlice';
import { Card, CardContent, CardHeader, CardTitle, Button } from '../components/ui';
import Modal from '../components/ui/Modal';
import { cn } from '../components/ui';

const Transactions = ({ user }) => {
  const dispatch = useDispatch();

  const selectedMonth = useSelector(state => state.transaction.selectedMonth);
  const totalBalance = useSelector(state => state.transaction.balance);
  const selectedDate = useSelector(state => state.transaction.selectedDate);
  const groupedSortedIncomes = useSelector(state => state.transaction.groupedAndSortedIncomes);
  const groupedSortedTransactions = useSelector(state => state.transaction.groupedAndSortedTransactions);
  const monthlySavings = useSelector(state => state.transaction.savings);
  const monthlyIncome = useSelector(state => state.transaction.monthlyIncome);
  const transactions = useSelector(state => state.transaction.transactions);
  const incomeArray = useSelector(state => state.transaction.incomeArray);
  const categoryArray = useSelector(state => state.transaction.categoryArray);
  const labelArray = useSelector(state => state.transaction.labelArray);

  const [showModal, setShowModal] = useState(false);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [isCalendarVisible, setCalendarVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [keepOpen, setKeepOpen] = useState(false);
  const [totalExpense, setTotalExpense] = useState(0);
  const [filterCategory, setFilterCategory] = useState('All');
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isFabOpen, setIsFabOpen] = useState(false);

  const [income, setIncome] = useState({
    date: new Date(),
    amount: '',
    notes: '',
    category: ''
  });

  const [transaction, setTransaction] = useState({
    category: '',
    amount: '',
    notes: '',
    label: ''
  });

  useEffect(() => {
    fetchCategories();
    fetchLabels();
  }, []);

  useEffect(() => {
    if (user !== undefined) {
      setIsLoading(true);
      fetchTransactions(selectedMonth);
    }
  }, [selectedMonth, user]);

  useEffect(() => {
    calculateTotalExpense();
  }, [transactions]);

  useEffect(() => {
    calculateMonthlyIncome();
  }, [incomeArray]);

  const handleChange = (event) => {
    setTransaction({ ...transaction, [event.target.name]: event.target.value });
  };

  const handleIncomeChange = (event) => {
    setIncome({ ...income, [event.target.name]: event.target.value });
  };

  const fetchTransactions = async (selectedMonth) => {
    try {
      const response = await api.Transaction.listByMonth(user?.sub, selectedMonth);
      if (response.status === 200) {
        dispatch(setTransactions(response.data.transactions));
        groupAndSortByDate(response.data.transactions, setSortedTransactions);
        dispatch(setSavings(response.data.savings));
        dispatch(setIncomeArray(response.data.incomes));
        groupAndSortByDate(response.data.incomes, setSortedIncomes);
        dispatch(setBalance(response.data.balance));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.Category.list(user?.sub);
      if (response.status === 200) {
        dispatch(setCategoryArray(response.data));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchLabels = async () => {
    try {
      const response = await api.Label.list(user?.sub);
      if (response.status === 200) {
        dispatch(setLabelArray(response.data));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const groupAndSortByDate = (itemArray, setItemArrayAction) => {
    const grouped = groupBy(itemArray, (item) => format(new Date(item.date), 'dd MMM yy'));
    const sorted = Object.entries(grouped).sort((a, b) => new Date(b[0]) - new Date(a[0]));
    dispatch(setItemArrayAction(Object.fromEntries(sorted)));
  };

  const calculateTotalExpense = () => {
    let tempExpense = 0;
    transactions.forEach(t => tempExpense += t.amount);
    setTotalExpense(tempExpense);
  };

  const calculateMonthlyIncome = () => {
    let tempIncome = 0;
    incomeArray.forEach(i => tempIncome += i.amount);
    dispatch(setMonthlyIncome(tempIncome));
  };

  const handleAddIncome = async () => {
    if (!income.amount || !income.category) {
      alert('Please enter an amount and category');
      return;
    }
    setSubmitting(true);
    try {
      const incomeData = {
        date: income.date.toISOString().substring(0, 10),
        amount: income.amount,
        notes: income.notes,
        category: income.category
      };

      let response;
      if (isEditing) {
        response = await api.Income.update({
          userId: user.sub,
          incomeId: editId,
          income: incomeData,
          date: incomeData.date
        });
      } else {
        response = await api.Income.insert({ userId: user.sub, income: incomeData });
      }

      if (response.status === 200) {
        setIncome({ date: new Date(), amount: '', notes: '', category: '' });
        setShowIncomeModal(false);
        setIsEditing(false);
        setEditId(null);
        fetchTransactions(selectedMonth);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditIncome = (item) => {
    setIncome({
      date: new Date(item.date),
      amount: item.amount,
      notes: item.notes,
      category: item.category
    });
    setEditId(item._id);
    setIsEditing(true);
    setShowIncomeModal(true);
  };

  const handleAddTransaction = async () => {
    if (!transaction.category || !transaction.amount) {
      alert('Please select a category and enter an amount');
      return;
    }
    setSubmitting(true);
    try {
      const transactionData = {
        category: transaction.category,
        date: selectedDate.toISOString().substring(0, 10),
        amount: transaction.amount,
        notes: transaction.notes,
        label: transaction.label,
        keywords: transaction.keywords || []
      };

      let response;
      if (isEditing) {
        response = await api.Transaction.update({
          userId: user.sub,
          transactionId: editId,
          transaction: transactionData,
          date: transactionData.date
        });
      } else {
        response = await api.Transaction.insert({ userId: user.sub, transaction: transactionData });
      }

      if (response.status === 200) {
        setTransaction({ category: '', amount: '', notes: '', label: '', keywords: [] });
        fetchTransactions(selectedMonth);
        if (!keepOpen) {
          setShowModal(false);
          setIsEditing(false);
          setEditId(null);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTransaction = (item) => {
    setTransaction({
      category: item.category,
      amount: item.amount,
      notes: item.notes,
      label: item.label || '',
      keywords: item.keywords || []
    });
    dispatch(setSelectedDate(new Date(item.date)));
    setEditId(item._id);
    setIsEditing(true);
    setShowModal(true);
  };

  const goToNextMonth = () => {
    const nextMonth = new Date(selectedMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    dispatch(setCurrentMonth(nextMonth));
  };

  const goToPreviousMonth = () => {
    const previousMonth = new Date(selectedMonth);
    previousMonth.setMonth(previousMonth.getMonth() - 1);
    dispatch(setCurrentMonth(previousMonth));
  };

  const KPI_CARDS = [
    { title: 'Balance', value: totalBalance, icon: Wallet, color: 'text-blue-600 dark:text-blue-400', bgIcon: 'bg-blue-50 dark:bg-blue-950/50' },
    { title: 'Savings', value: monthlySavings, icon: PiggyBank, color: monthlySavings < 0 ? 'text-red-500' : 'text-emerald-500', bgIcon: 'bg-red-50 dark:bg-red-950/50' },
    { title: 'Income', value: monthlyIncome, icon: TrendingUp, color: 'text-emerald-500', bgIcon: 'bg-emerald-50 dark:bg-emerald-950/50' },
    { title: 'Expenses', value: totalExpense, icon: TrendingDown, color: 'text-red-500', bgIcon: 'bg-red-50 dark:bg-red-950/50' },
  ];

  const filteredTransactions = filterCategory === 'All'
    ? groupedSortedTransactions
    : Object.fromEntries(
      Object.entries(groupedSortedTransactions).map(([date, items]) => [
        date,
        items.filter(item => item.category === filterCategory)
      ]).filter(([_, items]) => items.length > 0)
    );

  const selectedValueForLabel = labelArray.find(label => label?.labelName?.toLowerCase() === transaction?.label?.toLowerCase());

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-10">

      {/* DESKTOP HEADER & CONTROLS */}
      <div className="hidden md:flex items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-foreground">Transactions</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage your detailed income and expense history.</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Month Switcher Chip */}
          <div className="flex items-center gap-1 bg-card border border-border/80 p-1 rounded-xl shadow-xs">
            <Button variant="ghost" size="icon" onClick={goToPreviousMonth} className="h-8 w-8 rounded-lg">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="relative">
              <Button
                variant="ghost"
                className="px-3 py-1 font-semibold text-sm min-w-[140px] h-8 rounded-lg"
                onClick={() => setCalendarVisible(!isCalendarVisible)}
              >
                <CalendarIcon className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                {selectedMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </Button>
              {isCalendarVisible && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 z-[100] bg-card border border-border rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in slide-in-from-top-2 duration-200">
                  <DatePicker
                    selected={selectedMonth}
                    onChange={(date) => { dispatch(setCurrentMonth(date)); setCalendarVisible(false); }}
                    dateFormat="MMMM yyyy"
                    showMonthYearPicker
                    inline
                  />
                </div>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={goToNextMonth} className="h-8 w-8 rounded-lg">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <Button
            onClick={() => { setIsEditing(false); setShowIncomeModal(true); }}
            variant="outline"
            className="rounded-xl border-blue-600/30 text-blue-600 hover:bg-blue-50 dark:border-blue-500/30 dark:text-blue-400 dark:hover:bg-blue-950/30 font-semibold h-10 px-4"
          >
            <ArrowUpRight className="w-4 h-4 mr-1.5" />
            Add Income
          </Button>

          <Button
            onClick={() => { setIsEditing(false); setShowModal(true); }}
            className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md shadow-blue-600/20 h-10 px-4"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add Transaction
          </Button>
        </div>
      </div>

      {/* DESKTOP STAT CARDS GRID */}
      <div className="hidden md:grid gap-4 grid-cols-2 lg:grid-cols-4">
        {KPI_CARDS.map((card) => (
          <Card key={card.title} className="rounded-2xl border border-border/60 shadow-xs bg-card hover:border-border transition-all">
            <CardHeader className="flex flex-row items-center justify-between pb-1 px-4 pt-4">
              <CardTitle className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{card.title}</CardTitle>
              <div className={`w-8 h-8 rounded-full ${card.bgIcon} flex items-center justify-center`}>
                <card.icon className={cn("w-4 h-4", card.color)} />
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className={cn("text-2xl font-bold tracking-tight", card.value < 0 && "text-red-600 dark:text-red-400")}>
                {card.value < 0 ? '-' : ''}₹{Math.abs(card.value).toLocaleString()}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* MOBILE HERO CARD (Total Balance + 3 Subcards: Savings, Income, Expenses) */}
      <div className="block md:hidden">
        <div className="bg-gradient-to-br from-blue-600 via-blue-600 to-blue-700 rounded-3xl p-3 text-white shadow-xl shadow-blue-600/25 relative overflow-hidden">
          {/* Top section inside hero card */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold tracking-widest uppercase text-blue-100/90">Total Balance</span>
            <div className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20">
              <Wallet className="w-5 h-5 text-white" />
            </div>
          </div>

          <div className="mt-2 text-3xl font-extrabold tracking-tight">
            ₹{totalBalance.toLocaleString()}
          </div>

          {/* 3 Subcards Grid (Savings, Income, Expenses) */}
          <div className="grid grid-cols-3 gap-2.5 mt-5">
            <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-2.5">
              <div className="flex items-center justify-between text-[10px] uppercase font-bold tracking-wider text-blue-100">
                <span>Savings</span>
                <TrendingDown className="w-3.5 h-3.5 text-red-300" />
              </div>
              <div className="text-sm font-bold mt-1 text-white truncate">
                {monthlySavings < 0 ? '-' : ''}₹{Math.abs(monthlySavings).toLocaleString()}
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-2.5">
              <div className="flex items-center justify-between text-[10px] uppercase font-bold tracking-wider text-blue-100">
                <span>Income</span>
                <TrendingUp className="w-3.5 h-3.5 text-emerald-300" />
              </div>
              <div className="text-sm font-bold mt-1 text-white truncate">
                ₹{monthlyIncome.toLocaleString()}
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-2.5">
              <div className="flex items-center justify-between text-[10px] uppercase font-bold tracking-wider text-blue-100">
                <span>Expenses</span>
                <TrendingDown className="w-3.5 h-3.5 text-red-300" />
              </div>
              <div className="text-sm font-bold mt-1 text-white truncate">
                ₹{totalExpense.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* HISTORY CONTAINER CARD & LIST */}
      <Card className="rounded-2xl border border-border/60 shadow-xs overflow-hidden bg-card">
        <CardHeader className="bg-muted/20 border-b border-border/60 py-3.5 px-4 md:px-6 flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-bold text-foreground">History</CardTitle>

          {/* Month Switcher for Mobile & Filter for both */}
          <div className="flex items-center gap-2">
            {/* Mobile Month Switcher Chip */}
            <div className="flex md:hidden items-center bg-background border border-border rounded-xl px-1 py-0.5 shadow-xs">
              <Button variant="ghost" size="icon" onClick={goToPreviousMonth} className="h-7 w-7">
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <div className="relative">
                <button
                  className="px-1.5 py-1 text-xs font-bold text-foreground flex items-center gap-1"
                  onClick={() => setCalendarVisible(!isCalendarVisible)}
                >
                  <CalendarIcon className="w-3.5 h-3.5 text-blue-600" />
                  {selectedMonth.toLocaleString('default', { month: 'short', year: '2-digit' })}
                </button>
                {isCalendarVisible && (
                  <div className="absolute top-full mt-2 right-0 z-[100] bg-card border border-border rounded-xl shadow-xl overflow-hidden">
                    <DatePicker
                      selected={selectedMonth}
                      onChange={(date) => { dispatch(setCurrentMonth(date)); setCalendarVisible(false); }}
                      dateFormat="MMMM yyyy"
                      showMonthYearPicker
                      inline
                    />
                  </div>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={goToNextMonth} className="h-7 w-7">
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>

            {/* Filter Dropdown Toggle */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                className={cn("h-8 rounded-xl text-xs font-semibold text-muted-foreground border border-border/60", filterCategory !== 'All' && "text-blue-600 border-blue-600/30 bg-blue-50 dark:bg-blue-950/40")}
                onClick={() => setIsFilterVisible(!isFilterVisible)}
              >
                <Filter className="w-3.5 h-3.5 mr-1.5" />
                {filterCategory === 'All' ? 'Filter' : filterCategory}
              </Button>
              {isFilterVisible && (
                <div className="absolute right-0 top-full mt-2 z-[100] bg-card border border-border rounded-2xl shadow-xl p-2 min-w-[200px] animate-in fade-in zoom-in slide-in-from-top-2">
                  <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2 px-3 pt-1">Filter by Category</div>
                  <div className="space-y-1">
                    <button
                      className={cn("w-full text-left px-3 py-1.5 text-xs font-semibold rounded-xl hover:bg-muted transition-colors", filterCategory === 'All' && "bg-blue-600/10 text-blue-600 font-bold")}
                      onClick={() => { setFilterCategory('All'); setIsFilterVisible(false); }}
                    >
                      All Categories
                    </button>
                    {categoryArray.map(cat => (
                      <button
                        key={cat._id}
                        className={cn("w-full text-left px-3 py-1.5 text-xs font-semibold rounded-xl hover:bg-muted transition-colors", filterCategory === cat.categoryName && "bg-blue-600/10 text-blue-600 font-bold")}
                        onClick={() => { setFilterCategory(cat.categoryName); setIsFilterVisible(false); }}
                      >
                        {cat.categoryName}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-3 md:p-6">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-4 animate-pulse">
                  <div className="bg-muted rounded-full w-11 h-11" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-1/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {Object.keys(groupedSortedIncomes).length === 0 && Object.keys(filteredTransactions).length === 0 && (
                <div className="p-12 text-center text-muted-foreground italic flex flex-col items-center">
                  <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mb-4">
                    <Receipt className="w-8 h-8 opacity-30 text-blue-600" />
                  </div>
                  <p className="font-medium text-sm">No transaction records found for this month.</p>
                </div>
              )}

              {/* Render Income items grouped by date */}
              {Object.keys(groupedSortedIncomes).map(date => (
                <div key={`income-${date}`}>
                  <div className="flex items-center gap-3 my-3">
                    <span className="text-[10px] font-bold text-muted-foreground tracking-widest uppercase shrink-0">
                      {date}
                    </span>
                    <div className="flex-1 h-px bg-border/60" />
                  </div>
                  {groupedSortedIncomes[date].map(item => (
                    <IncomeItem
                      key={item._id}
                      {...item}
                      id={item._id}
                      userId={user?.sub}
                      fetchMonthlyIncome={() => fetchTransactions(selectedMonth)}
                      selectedMonth={selectedMonth}
                      onEdit={() => handleEditIncome(item)}
                    />
                  ))}
                </div>
              ))}

              {/* Render Transactions grouped by date */}
              {Object.keys(filteredTransactions).map(date => (
                <div key={date}>
                  <div className="flex items-center gap-3 my-3">
                    <span className="text-[10px] font-bold text-muted-foreground tracking-widest uppercase shrink-0">
                      {date}
                    </span>
                    <div className="flex-1 h-px bg-border/60" />
                  </div>
                  {filteredTransactions[date].map(item => (
                    <TransactionItem
                      key={item._id}
                      {...item}
                      id={item._id}
                      userId={user?.sub}
                      fetchTransactions={fetchTransactions}
                      selectedMonth={selectedMonth}
                      onEdit={() => handleEditTransaction(item)}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* MOBILE SPEED DIAL FLOATING ACTION BUTTON */}
      <div className="fixed bottom-20 right-4 z-40 md:hidden flex flex-col items-end gap-2.5">
        {/* Backdrop trigger overlay when open */}
        {isFabOpen && (
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-xs z-30 md:hidden"
            onClick={() => setIsFabOpen(false)}
          />
        )}

        {/* Speed Dial Options */}
        {isFabOpen && (
          <div className="flex flex-col items-end gap-2.5 z-40 mb-1 animate-in fade-in slide-in-from-bottom-3 duration-200">
            <button
              onClick={() => {
                setIsEditing(false);
                setShowIncomeModal(true);
                setIsFabOpen(false);
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-lg shadow-emerald-600/30 active:scale-95 transition-all"
            >
              <span>Add Income</span>
              <ArrowUpRight className="w-4 h-4" />
            </button>

            <button
              onClick={() => {
                setIsEditing(false);
                setShowModal(true);
                setIsFabOpen(false);
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs shadow-lg shadow-blue-600/30 active:scale-95 transition-all"
            >
              <span>Add Expense</span>
              <Receipt className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Main Floating Trigger Button */}
        <button
          onClick={() => setIsFabOpen(!isFabOpen)}
          className={`z-40 rounded-full w-14 h-14 shadow-xl flex items-center justify-center transition-all duration-300 active:scale-95 ${isFabOpen
            ? 'bg-slate-800 text-white shadow-slate-900/40 rotate-45'
            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-600/30'
            }`}
          aria-label="Toggle actions"
          title="Add Income or Expense"
        >
          <Plus className="w-7 h-7 transition-transform duration-300" />
        </button>
      </div>

      {/* ADD / EDIT INCOME MODAL */}
      <Modal
        isOpen={showIncomeModal}
        onClose={() => { setShowIncomeModal(false); setIsEditing(false); setEditId(null); }}
        title={
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-500">
              <TrendingUp className="w-5 h-5" />
            </div>
            <span className="font-semibold text-foreground">
              {isEditing ? "Edit Income" : "Add Income"}
            </span>
          </div>
        }
        footer={
          <div className="flex items-center justify-end gap-2.5 w-full">
            <Button
              variant="ghost"
              onClick={() => { setShowIncomeModal(false); setIsEditing(false); setEditId(null); }}
              className="px-4 py-2 text-sm font-medium rounded-xl hover:bg-muted/80"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddIncome}
              disabled={submitting}
              className="px-5 py-2 text-sm font-medium rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20 hover:shadow-emerald-500/30 transition-all duration-200"
            >
              {submitting ? (isEditing ? 'Saving...' : 'Adding...') : (isEditing ? 'Save Changes' : 'Add Income')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 pt-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <CalendarIcon className="w-3.5 h-3.5 text-emerald-500" />
                Date
              </label>
              <DatePicker
                selected={income.date}
                onChange={(date) => setIncome({ ...income, date })}
                className="w-full h-11 rounded-xl border border-input/80 bg-background px-3.5 py-2.5 text-sm font-medium focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all shadow-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5 text-emerald-500" />
                Amount
              </label>
              <input
                type="number"
                name="amount"
                value={income.amount}
                onChange={handleIncomeChange}
                className="w-full h-11 rounded-xl border border-input/80 bg-background px-3.5 py-2.5 text-sm font-semibold focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all shadow-sm"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-emerald-500" />
              Category
            </label>
            <div className="relative">
              <select
                name="category"
                value={income.category}
                onChange={handleIncomeChange}
                className="w-full h-11 rounded-xl border border-input/80 bg-background px-3.5 py-2.5 text-sm font-medium focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all cursor-pointer shadow-sm appearance-none pr-8"
              >
                <option value="">Select Category</option>
                <option value="Salary">Salary</option>
                <option value="Bonus">Bonus</option>
                <option value="Gifts">Gifts</option>
                <option value="Freelancing">Freelancing</option>
                <option value="Other">Other</option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
                <ChevronRight className="w-4 h-4 rotate-90" />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-emerald-500" />
              Notes
            </label>
            <textarea
              name="notes"
              value={income.notes}
              onChange={handleIncomeChange}
              className="w-full rounded-xl border border-input/80 bg-background px-3.5 py-2.5 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all shadow-sm min-h-[90px] resize-none"
              placeholder="Add some notes about this income..."
            />
          </div>
        </div>
      </Modal>

      {/* ADD / EDIT TRANSACTION MODAL */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setIsEditing(false); setEditId(null); }}
        title={
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-blue-600/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
              <Receipt className="w-5 h-5" />
            </div>
            <span className="font-semibold text-foreground">
              {isEditing ? "Edit Transaction" : "Add Transaction"}
            </span>
          </div>
        }
        footer={
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="keepOpen"
                checked={keepOpen}
                onChange={() => setKeepOpen(!keepOpen)}
                className="w-4 h-4 rounded border-input text-blue-600 focus:ring-blue-600 accent-blue-600 cursor-pointer"
              />
              <label htmlFor="keepOpen" className="text-sm font-medium text-muted-foreground hover:text-foreground cursor-pointer select-none">
                Keep open
              </label>
            </div>
            <div className="flex items-center gap-2.5">
              <Button
                variant="ghost"
                onClick={() => { setShowModal(false); setIsEditing(false); setEditId(null); }}
                className="px-2 md:px-4 py-2 text-sm font-medium rounded-xl hover:bg-muted/80"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddTransaction}
                disabled={submitting}
                className="px-5 py-2 text-sm font-medium rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-600/20 hover:shadow-blue-600/30 transition-all duration-200"
              >
                {submitting ? (isEditing ? 'Saving...' : 'Adding...') : (isEditing ? 'Save' : 'Add')}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4 pt-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                Category
              </label>
              <div className="relative">
                <select
                  name="category"
                  value={transaction.category}
                  onChange={handleChange}
                  className="w-full h-11 rounded-xl border border-input/80 bg-background px-3.5 py-2.5 text-sm font-medium focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition-all cursor-pointer shadow-sm appearance-none pr-8"
                >
                  <option value="">Select Category</option>
                  {categoryArray.map((cat) => (
                    <option key={cat._id} value={cat.categoryName}>{cat.categoryName}</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
                  <ChevronRight className="w-4 h-4 rotate-90" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                Label
              </label>
              <div className="relative">
                <select
                  name="label"
                  value={selectedValueForLabel?.labelName || ''}
                  onChange={handleChange}
                  className="w-full h-11 rounded-xl border border-input/80 bg-background px-3.5 py-2.5 text-sm font-medium focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition-all cursor-pointer shadow-sm appearance-none pr-8"
                >
                  <option value="">Select Label</option>
                  {labelArray.map((lbl) => (
                    <option key={lbl._id} value={lbl.labelName}>{lbl.labelName}</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
                  <ChevronRight className="w-4 h-4 rotate-90" />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <CalendarIcon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                Date
              </label>
              <DatePicker
                selected={selectedDate}
                onChange={(date) => dispatch(setSelectedDate(date))}
                className="w-full h-11 rounded-xl border border-input/80 bg-background px-3.5 py-2.5 text-sm font-medium focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition-all shadow-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                Amount
              </label>
              <input
                type="number"
                name="amount"
                value={transaction.amount}
                onChange={handleChange}
                className="w-full h-11 rounded-xl border border-input/80 bg-background px-3.5 py-2.5 text-sm font-semibold focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition-all shadow-sm"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
              Notes
            </label>
            <textarea
              name="notes"
              value={transaction.notes}
              onChange={handleChange}
              className="w-full rounded-xl border border-input/80 bg-background px-3.5 py-2.5 text-sm focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition-all shadow-sm min-h-[90px] resize-none"
              placeholder="Add some notes about this transaction..."
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Transactions;